import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { usePusher } from '../contexts/PusherContext';
import { useAuth0 } from '@auth0/auth0-react';

interface DependencyUpdate {
  table: string;
  recordId: string;
  timestamp: number;
}

interface DependencyUpdateContextType {
  registerCallback: (
    table: string,
    recordId: string,
    callback: (update: DependencyUpdate) => void,
  ) => () => void;
}

const DependencyUpdateContext =
  createContext<DependencyUpdateContextType | null>(null);

interface DependencyUpdateProviderProps {
  children: React.ReactNode;
}

export const DependencyUpdateProvider: React.FC<
  DependencyUpdateProviderProps
> = ({ children }) => {
  const pusherContext = usePusher(true); // Get the full pusher context
  const callbacksRef = useRef<
    Map<string, Set<(update: DependencyUpdate) => void>>
  >(new Map());

  // Shared handler for dependency updates (from Pusher or test events)
  const handleDependencyUpdate = useCallback((data: DependencyUpdate) => {
    console.log('Received dependency update:', data);

    // Notify all registered callbacks for this table/record combination
    const key = `${data.table}:${data.recordId}`;
    const callbacks = callbacksRef.current.get(key);
    console.log('Found callbacks:', callbacks);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error executing dependency update callback:', error);
        }
      });
    }

    // Also notify callbacks registered for just the table (without specific record ID)
    const tableCallbacks = callbacksRef.current.get(data.table);
    if (tableCallbacks) {
      tableCallbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(
            'Error executing table-level dependency update callback:',
            error,
          );
        }
      });
    }
  }, []);

  // Listen for dependency updates on the existing pusher channel
  useEffect(() => {
    if (!pusherContext?.pusherChannel) {
      console.log(
        'Pusher channel not available yet, will retry when available',
      );
      return;
    }

    // Bind to the dependency-update event on the existing channel
    pusherContext.pusherChannel.bind(
      'dependency-update',
      handleDependencyUpdate,
    );
    console.log('Listening for dependency updates on existing Pusher channel');

    // Cleanup on unmount
    return () => {
      pusherContext.pusherChannel.unbind(
        'dependency-update',
        handleDependencyUpdate,
      );
    };
  }, [pusherContext, handleDependencyUpdate]);

  // Listen for test dependency updates (development only)
  useEffect(() => {
    const handleTestUpdate = (event: CustomEvent) => {
      console.log('🧪 Test dependency update received:', event.detail);
      handleDependencyUpdate(event.detail);
    };

    window.addEventListener(
      'test-dependency-update',
      handleTestUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        'test-dependency-update',
        handleTestUpdate as EventListener,
      );
    };
  }, [handleDependencyUpdate]);

  const registerCallback = (
    table: string,
    recordId: string,
    callback: (update: DependencyUpdate) => void,
  ): (() => void) => {
    const key = recordId ? `${table}:${recordId}` : table;

    if (!callbacksRef.current.has(key)) {
      callbacksRef.current.set(key, new Set());
    }

    callbacksRef.current.get(key)!.add(callback);

    // Return unregister function
    return () => {
      const callbacks = callbacksRef.current.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          callbacksRef.current.delete(key);
        }
      }
    };
  };

  const contextValue: DependencyUpdateContextType = {
    registerCallback,
  };

  return (
    <DependencyUpdateContext.Provider value={contextValue}>
      {children}
    </DependencyUpdateContext.Provider>
  );
};

export const useDependencyUpdates = (): DependencyUpdateContextType => {
  const context = useContext(DependencyUpdateContext);
  if (!context) {
    throw new Error(
      'useDependencyUpdates must be used within a DependencyUpdateProvider',
    );
  }
  return context;
};
